import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { Button } from '@/components/ui/button';
import { Network, Zap, DollarSign, Globe, ArrowRight, CheckCircle, Users, Cpu, Cable } from 'lucide-react';

const AshburnInterconnect = () => {
  const scrollToLearnMore = () => {
    const learnMoreSection = document.getElementById('learn-more-section');
    if (learnMoreSection) {
      learnMoreSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnMoreClick = () => {
    window.open('https://ncolo.net', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata
        title="Ashburn Interconnect | High-Speed Affordable Ashburn Colocation | NEXX TELECOM"
        description="Get ultra-low latency, high-throughput connectivity between Maryland and Ashburn's data center ecosystem without paying Ashburn colocation rates."
        keywords="ashburn interconnect, affordable ashburn colocation, maryland data center, low latency connectivity, ashburn peering"
        canonical="https://nexx.net/ashburn-interconnect"
      />
      
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 to-white py-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-blue-600/10 animate-pulse"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 animate-fade-in">
                Ashburn Interconnect
              </h1>
              <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay">
                Your High-Speed Pipe to <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">Affordable Ashburn Colocation</a>
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
              {/* Partnership Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-3">
                  <div className="bg-blue-600 rounded-full p-2 mr-3">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Industry Partnership</h3>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  At NEXX, we <span className="font-semibold text-blue-600">partner with industry leaders</span> to deliver cutting-edge solutions. 
                  That's why we proudly offer the Ashburn Interconnect through our strategic partnership with <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">NCOLO</a>.
                </p>
                <div className="flex items-center text-blue-600 font-medium text-sm">
                  <Cable className="h-4 w-4 mr-2" />
                  <span>Dedicated network pipe linking Maryland to Ashburn's data center ecosystem</span>
                </div>
              </div>

              {/* Revolutionary Infrastructure Section */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center mb-3">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full p-2 mr-3">
                    <Cpu className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Revolutionary Infrastructure</h3>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  This isn't just another backhaul or peering lane. <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">NCOLO's</a> Ashburn Interconnect is a 
                  <span className="font-semibold text-blue-600"> revolutionary infrastructure layer</span>, designed for:
                </p>
                
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                    <div className="bg-blue-100 rounded-full p-1.5 mr-2">
                      <Zap className="h-3 w-3 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">Ultra-low latency</span>
                  </div>
                  <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                    <div className="bg-blue-100 rounded-full p-1.5 mr-2">
                      <Network className="h-3 w-3 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">High-throughput connectivity</span>
                  </div>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm text-blue-800">
                    <strong>Direct connection:</strong> Maryland operations ↔ Global carriers in Ashburn, VA
                  </p>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  onClick={scrollToLearnMore}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 hover:scale-105 transform transition-all duration-300 hover:shadow-lg"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Powered by NCOLO Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Built for Scale, Powered by NCOLO
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Through our partnership with <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">NCOLO</a>, we bring you their renowned 
                reliability, speed, and routing intelligence to power your infrastructure needs.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center p-6">
                <DollarSign className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Host workloads in Maryland
                </h3>
                <p className="text-gray-600">
                  At significantly lower costs than traditional Ashburn facilities
                </p>
              </div>
              
              <div className="text-center p-6">
                <Globe className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Peer with major carriers
                </h3>
                <p className="text-gray-600">
                  Access CDNs via Ashburn without racking in Ashburn itself
                </p>
              </div>
              
              <div className="text-center p-6">
                <Zap className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Expand AI & proxy infrastructure
                </h3>
                <p className="text-gray-600">
                  Using metro-grade routing for optimal performance
                </p>
              </div>
            </div>

            <div className="text-center bg-gray-50 rounded-xl p-8">
              <p className="text-lg text-gray-700 mb-4">
                All while taking advantage of{' '}
                <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">affordable Ashburn colocation</a>
                {' '}— without paying Ashburn colocation rates.
              </p>
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Why It Matters
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Ashburn is the internet's capital, home to the largest internet exchanges and 
                  carrier density in North America. But for many growing companies, racking in 
                  Ashburn is cost-prohibitive.
                </p>
                <p className="text-gray-600 mb-6">
                  NCOLO's Ashburn Interconnect removes that barrier. Here's what you get:
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                  <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Proximity without the price tag</h3>
                    <p className="text-gray-600">Access Ashburn's ecosystem at Maryland prices, saving up to 60% on colocation costs</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                  <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Redundancy with rapid deployment</h3>
                    <p className="text-gray-600">Multiple paths ensure maximum reliability with deployment in days, not months</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                  <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Scalable hosting infrastructure</h3>
                    <p className="text-gray-600">Seamlessly burst into Ashburn capacity when needed, scaling with your growth</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* A New Way to Think About Edge */}
        <section className="py-16 bg-white" id="learn-more-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 animate-fade-in">
                A New Way to Think About Edge
              </h2>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed animate-fade-in-delay">
                Whether you're running AI models, building residential proxy networks, or scaling 
                global applications, <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">NCOLO's</a> Ashburn Interconnect allows you to operate with the speed 
                and scale of Ashburn — without ever leaving Maryland.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-600/5 to-blue-50 rounded-2xl p-12 text-center animate-fade-in hover:scale-105 transition-transform duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 animate-fade-in">
                You don't need another data center.
              </h3>
              <h3 className="text-2xl font-bold text-blue-600 mb-6 animate-fade-in-delay">
                <span className="text-blue-600">You need the right pipe.</span>
              </h3>
              <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto animate-fade-in">
                Let <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">NCOLO</a> power your growth with the Ashburn Interconnect — where infrastructure 
                meets intelligence, and{' '}
                <a href="https://ncolo.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">affordable Ashburn colocation</a>
                {' '}becomes your new standard.
              </p>
              <Button 
                onClick={handleLearnMoreClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg animate-fade-in-delay hover:shadow-lg transition-all duration-300"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AshburnInterconnect;
